/**
 * The one generator for the ids that anchor rich-text content.
 *
 * A block id is a **stored cross-runtime contract**, not a local convenience. The editor
 * stamps one onto every block node it saves; the Convex draft projection stamps one onto
 * every block an assistant writes; clarifications, evidence citations and tab diffs then
 * address stored content *by that id*. An id minted on one side has to be readable — and,
 * crucially, not collide — on the other, so the shape of the string is owned here rather
 * than re-typed per surface.
 *
 * Everything below is pure and runtime-neutral on purpose: it runs in the browser, in
 * Convex's edge runtime, and in Node test suites, and reads nothing but `crypto` and
 * `Math.random`.
 */
/** Prefix on every block-anchor id. Stored in `attrs.blockId` on rich-text block nodes. */
export const BLOCK_ID_PREFIX = "blk_";
/** Prefix on every catalog-embed id. Stored in `attrs.embedId` on catalog embed nodes. */
export const CATALOG_EMBED_ID_PREFIX = "catalog_";
/**
 * `${prefix}${uuid}`, or `${prefix}${ten random base-36 characters}` where
 * `crypto.randomUUID` is unavailable.
 *
 * The fallback exists because this runs in more than one runtime and `randomUUID` is absent
 * from insecure browser contexts. It is weaker — roughly 52 bits of entropy against a
 * UUIDv4's 122 — which is why it is a fallback and not the default: collisions are exactly
 * what breaks the addressing this contract rests on.
 */
function createPrefixedId(prefix) {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return `${prefix}${crypto.randomUUID()}`;
    }
    return `${prefix}${Math.random().toString(36).slice(2, 12)}`;
}
/** A fresh id for a rich-text block node. */
export function createBlockId() {
    return createPrefixedId(BLOCK_ID_PREFIX);
}
/** A fresh id for a catalog embed node. */
export function createCatalogEmbedId() {
    return createPrefixedId(CATALOG_EMBED_ID_PREFIX);
}
/**
 * The stored id if the node carries a usable one, else `null` — the caller then mints a
 * replacement with {@link createBlockId}.
 *
 * Blank-but-present counts as absent: a whitespace-only id addresses nothing, so a node
 * carrying one is re-stamped rather than left unaddressable. The value itself comes back
 * exactly as stored and is never trimmed — trimming here would silently rewrite ids that
 * other stored rows already point at.
 */
export function readBlockId(value) {
    return typeof value === "string" && value.trim().length > 0 ? value : null;
}
